import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {APPROVAL_STATUS, APPROVAL_TYPE, ApprovalStatus} from '../../../../constants';
import {trackEvent} from './../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateApprovalStatusMutation from '../../../../mutations/UpdateApprovalStatusMutation';
import {
	showApprovalErrorModal,
	showPartialApprovalFailureModal,
} from '../../../../components/timesheets/time-approval/ApprovalErrors';
import {ConfirmationModalPrimaryButton} from '../../../shared/components/modals/general/ConfirmationModal';
import {lastWorkingDayInWeek as resolveLastWorkingDayInWeek} from '../../../shared/util/TimesheetApprovalStatus';

/**
 * The date the submit modal should default to ending on.
 *
 * Submit sweeps from the beginning of time up to this date, so it decides how much of the week in
 * front of the owner the press actually covers — which is what PIKA-740 AC 1 and 2 are about: one
 * action clears the week, and no second one is left outstanding for it.
 *
 * Three rules:
 *
 * - **The week you are in stops at today.** Submitting the current week must not reach days that
 *   have not happened yet and lock them. A further press later in the same week is expected, and
 *   is a different thing from the two presses this ticket removes.
 * - **Except that it still reaches a declined row dated later in that week.** The rule above is
 *   about *new* time: a day that has not happened cannot hold work the owner has finished, so
 *   sweeping it up only locks it early. A declined row is not new time — it is already logged,
 *   has already been through an approver, and was already inside the lock the original submit
 *   set. Stopping at today leaves it behind, and because the week reads by what it still asks of
 *   its owner (`getApprovalStatusForWeek` ranks DECLINED first), the week goes on reading
 *   declined and goes on offering Submit — whose next press, defaulting to today again, matches
 *   no rows at all. So the end date runs out to the last declined day in the week when that is
 *   later than today, and no further — a week whose declined rows all fall on or before today is
 *   unaffected. The days in between go with it: the sweep is one contiguous range ending at that
 *   date, so a week declined on the Monday and the Friday, viewed on the Wednesday, takes
 *   Thursday along whether or not the approver ever touched it. That is what a single end date
 *   costs, and it buys the same one-press guarantee the whole change is for, applied to the week
 *   the owner is standing in.
 * - **Any other week runs to its last working day**, earlier or later. In Week view that value
 *   arrives as a prop. In Day view it does not: `TimesheetsPage` only computes it under
 *   `TIME_PERIOD.WEEK_VIEW` and otherwise falls back to the day being viewed, so trusting the
 *   prop ended a Day-view submit on, say, Wednesday and left Thursday's and Friday's entries
 *   behind. That is the second press arriving by date rather than by verb. Resolve the week's own
 *   last working day instead — `lastWorkingDayInWeek` derives the week from the date it is given,
 *   so Day view can ask it directly. A future week takes this branch too, which is what Week view
 *   has always done with one; the change is that Day view now agrees with it rather than ending
 *   on the day in view.
 *
 * @param lastDeclinedDateInWeek latest day in the viewed week holding a declined row, or null when
 *                               the week holds none
 * @param today injected so the current-week rule can be exercised without freezing the clock
 */
export const getSubmitEndDate = ({
	isWeekView,
	currentViewingDate,
	selectedPerson,
	lastWorkingDayInWeek,
	lastDeclinedDateInWeek = null,
	today = Moment(),
}: {
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	// TimesheetsPage_viewer['company']['person'], as the component takes it
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	selectedPerson: any;
	lastWorkingDayInWeek: Moment.Moment;
	lastDeclinedDateInWeek?: Moment.Moment | null;
	today?: Moment.Moment;
}): Moment.Moment => {
	if (today.isSame(currentViewingDate, 'week')) {
		return lastDeclinedDateInWeek?.isAfter(today, 'day') ? lastDeclinedDateInWeek : today;
	}
	return isWeekView ? lastWorkingDayInWeek : resolveLastWorkingDayInWeek(selectedPerson, currentViewingDate);
};

const submitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	lastWorkingDayInWeek,
	lastDeclinedDateInWeek,
	usingTimeOffApproval,
	hasApprovableTimeInWeek,
}) => {
	trackEvent('Submit Timesheets Bottom', 'Clicked', {
		// Person id, not email address: an address is content, which PIKA-772 §1 keeps out.
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.id : 'myself',
		newStatusesFlag: hasFeatureFlag('timesheet_approval_new_statuses'),
		approvalType: APPROVAL_TYPE.SUBMIT,
		// How often anyone submits a week holding nothing that needs approving. Asked for by Clare
		// Tuckerman (2026-09-15) to size whether approval of such weeks earns its keep at all: it
		// is the one case that still needs its own verb, and the honest answer to "should they keep
		// a separate route" eventually depends on how many people use them. Named for PIKA-740's
		// terminology rather than the code's "zero-hour week", which misleads — a week can be full
		// of internal time and still have nothing requiring approval.
		noTimeRequiringApproval: !hasApprovableTimeInWeek,
	});
	const submitDate = getSubmitEndDate({
		isWeekView,
		currentViewingDate,
		selectedPerson,
		lastWorkingDayInWeek,
		lastDeclinedDateInWeek,
	});
	showModal({
		type: MODAL_TYPE.SUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: submitDate,
		lockedDate,
		usingTimeOffApproval,
		// PIKA-740 criterion 11: the modal says what this press actually does when the week holds
		// nothing needing approval, which is advance the submission lock rather than send anything.
		hasApprovableTimeInWeek,
	});
};

export type TimesheetApprovalActions = {
	showSubmit: boolean;
	showResubmit: boolean;
	showUnsubmit: boolean;
};

/**
 * Decide which approval action the period offers.
 *
 * Under the new statuses the period offers exactly one, and the period's status alone chooses
 * it — so the badge the owner reads and the button they press always name the same next step:
 *
 *   pending             → Submit
 *   submitted           → Unsubmit
 *   declined            → Submit  (Resubmit, on a week with no time in it)
 *   approved, invoiced  → nothing; the period is settled
 *
 * A second button is not needed to reach outstanding time, because the status hierarchy that
 * feeds this ranks a period by what it still asks of its owner rather than by how far it has
 * got (see getApprovalStatusForWeek): a period holding unsubmitted rows reads pending even when
 * some of it has already gone to an approver, so Submit is what it offers.
 *
 * A declined period now resolves in **one** step rather than two. Submit's status clause reaches
 * DECLINED as well as PENDING and NOT_SUBMITTED (`submittableStatusClause`, TimeApprovalLogic.java),
 * so a single press sends the declined rows and anything logged while the period sat declined.
 * Before, Resubmit moved only the declined half and left the rest for a second press:
 *
 *   > If a week has both, some declined entries, some new ones, the person has to click twice.
 *   > We are wanting to have one button that picks up everything.
 *   >   — Clare Tuckerman, 2026-09-15, having hit it in a customer demo
 *
 * `hasSubmittableTimeRegs` has no say here; it is read only without the new statuses, where there
 * is no pending status for the hierarchy to rank and the choice stays lock-driven.
 *
 * ### Why a declined week with no time requiring approval keeps Resubmit
 *
 * Such a week has no rows to carry a status, so its decline is recorded on a `person_week_approval`
 * row instead, and only a verb that names the week can move it. SUBMIT cannot: `is0hWeekScope`
 * (TimeApprovalLogic.java) recognises an aligned single-week range only, while SUBMIT's range opens
 * at `LocalDate.MIN` — deliberately, because that is what lets one press clear a backlog. Teaching
 * SUBMIT the week path would mean either giving up the backlog sweep or having it scan for empty
 * declined weeks across all of history and flip them unseen.
 *
 * **"No time requiring approval" is not "an empty timesheet."** The predicate is
 * `hasApprovableTimeInWeek`, which counts only rows that carry an approval status
 * (`carriesApprovalStatus`) — so on a company with internal time approval switched off, a week of
 * nothing but internal time qualifies however full it looks, because those rows are born approved
 * and no verb can move them. Routing that week to Submit would offer a button matching no rows
 * that still reports success and advances the lock, leaving the week declined.
 *
 * Note this is deliberately **not** the same predicate as `getResubmitDateRange`'s
 * `hasTimeRegsInWeek`, which stays literal ("did they log anything at all") because it has to
 * agree with the back end's `weekHasNoTimeRegs`, which is unscoped by category on purpose. The two
 * diverge in one case — a week declined while empty that has since gained non-approvable hours —
 * and there the `person_week_approval` row is currently unreachable by either verb. Pre-existing,
 * and called out on PIKA-740 rather than fixed here, since closing it means changing what
 * `is0hWeekScope` considers an empty week.
 *
 * So the route stays separate, which costs no extra click: a week either holds time or it does not,
 * so exactly one of the two lights up. What it costs is a button label that varies with a state the
 * owner can see anyway. Keeping it separate also leaves 0h-week approval cheap to withdraw if the
 * tracking says it is not being used — Clare's preference, 2026-09-15: *"it does not really make
 * sense without a timesheets object, so I'd prefer to keep support minimal... I'd probably favour
 * empty weeks keep a separate route."*
 *
 * ### A company that approves time off but not timesheets
 *
 * A supported configuration — the time-off toggle is not nested under Timesheet approval — and it
 * gets a narrower answer. None of the timesheet verbs have scope there: project and internal rows
 * are born approved, so Submit and Unsubmit have nothing to act on. "Submit Time Off Only" has
 * reached a declined leave day since PIKA-730, so it covers a declined week that holds time. Only
 * the empty week is left over, and that is the same exception as above.
 *
 * @param periodApprovalStatus  aggregate status of the viewed day or week
 * @param hasSubmittableTimeRegs whether any row in the period is still awaiting submission
 * @param hasApprovableTimeInWeek whether the week holds any row that carries an approval status
 *                              — the predicate that separates the two declined routes
 * @param isWorkdaysLocked      whether the submission lock covers the period's working days
 * @param usingTimeApproval     whether the company puts timesheets through approval at all
 */
export const getTimesheetApprovalActions = ({
	periodApprovalStatus,
	hasSubmittableTimeRegs,
	hasApprovableTimeInWeek,
	hasNewStatuses,
	isWorkdaysLocked,
	usingTimeApproval,
}: {
	periodApprovalStatus: string | null;
	hasSubmittableTimeRegs: boolean;
	hasApprovableTimeInWeek: boolean;
	hasNewStatuses: boolean;
	isWorkdaysLocked: boolean;
	usingTimeApproval: boolean;
}): TimesheetApprovalActions => {
	// A declined week with no time requiring approval is the one case no submit button can reach,
	// because its status lives on a person_week_approval row rather than on any time reg. Shared by
	// both configurations below, since neither company's Submit can name a week.
	const isDeclinedWeekWithoutApprovableTime =
		hasNewStatuses && periodApprovalStatus === APPROVAL_STATUS.DECLINED && !hasApprovableTimeInWeek;

	if (!usingTimeApproval) {
		// Time off is the only thing under approval here, and "Submit Time Off Only" covers
		// sending it on — declined leave included, since PIKA-730. Only the empty week is left.
		return {
			showSubmit: false,
			showResubmit: isDeclinedWeekWithoutApprovableTime,
			showUnsubmit: false,
		};
	}

	if (!hasNewStatuses) {
		// Lock-driven rather than status-driven, as it has always been — but with the same
		// "still something to submit" escape, since a legacy client sees rows written under the
		// new statuses as unsubmitted and needs a way to send them on.
		//
		// No DECLINED case to consider: without the flag the read side projects that status to
		// NOT_SUBMITTED, so it never reaches this branch.
		const showUnsubmit = isWorkdaysLocked;
		return {showSubmit: !showUnsubmit || hasSubmittableTimeRegs, showResubmit: false, showUnsubmit};
	}

	switch (periodApprovalStatus) {
		case APPROVAL_STATUS.APPROVED:
		case APPROVAL_STATUS.INVOICED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: false};
		case APPROVAL_STATUS.DECLINED:
			// One press either way: Submit carries the declined rows and anything logged since,
			// except where the week holds nothing needing approval, so there is nothing for it to
			// carry and the week row needs the verb that names the week.
			return isDeclinedWeekWithoutApprovableTime
				? {showSubmit: false, showResubmit: true, showUnsubmit: false}
				: {showSubmit: true, showResubmit: false, showUnsubmit: false};
		case APPROVAL_STATUS.SUBMITTED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: true};
		default:
			// Pending, and the legacy unsubmitted status the read side projects onto it.
			return {showSubmit: true, showResubmit: false, showUnsubmit: false};
	}
};

/**
 * Compute the date range a Resubmit click should target.
 *
 * A week with no time registrations at all is settled by a week-scoped person-week-approval
 * row, and the back end's guard for that path only recognises a full-week range — so such a
 * week always operates at week granularity regardless of view mode.
 *
 * Otherwise: Week view = full-week range, Day view = single-day range (partial-day resubmit
 * preserved). The predicate spans every category of time, not just project time: a week
 * holding only internal time or time off has rows for the transition to act on, so it does
 * not need — and should not get — the whole-week widening.
 */
export const getResubmitDateRange = (
	currentViewingDate: Moment.Moment,
	isWeekView: boolean,
	hasTimeRegsInWeek: boolean
): {rangeStart: Moment.Moment; rangeEnd: Moment.Moment} => {
	const useWeekRange = isWeekView || !hasTimeRegsInWeek;
	return {
		rangeStart: useWeekRange ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone(),
		rangeEnd: useWeekRange ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone(),
	};
};

/**
 * The mutation input for a Resubmit click.
 *
 * `includeTimeOff` is not optional here. Internal time is in scope server-side wherever the
 * company approves it, but time off is opt-in per request — omit the flag and declined time off
 * is filtered out of the transition and stays declined however the owner reached this button.
 *
 * Since PIKA-730 it is no longer the *only* thing that can move such a row — "Submit Time Off
 * Only" reaches declined leave too — but Resubmit is the period-scoped verb and the one whose
 * audit entry records a correction rather than a first submission, so a Resubmit click that
 * quietly skipped the leave in the period it names would still be wrong.
 */
export const getResubmitInput = ({
	personId,
	currentViewingDate,
	isWeekView,
	hasTimeRegsInWeek,
}: {
	personId: string;
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
	hasTimeRegsInWeek: boolean;
}) => {
	const {rangeStart, rangeEnd} = getResubmitDateRange(currentViewingDate, isWeekView, hasTimeRegsInWeek);
	return {
		personIds: [personId],
		approvalType: APPROVAL_TYPE.RESUBMIT,
		includeTimeOff: true,
		startYear: rangeStart.year(),
		startMonth: rangeStart.month() + 1,
		startDay: rangeStart.date(),
		endYear: rangeEnd.year(),
		endMonth: rangeEnd.month() + 1,
		endDay: rangeEnd.date(),
	};
};

/**
 * Which toast a "Submit Time Off Only" click has earned.
 *
 * The button is a backlog sweep, not a period action: `handleSubmitTimeOffFetch`
 * (TimeApprovalLogic.java:146) hardcodes LocalDate.MIN..MAX and the mutation input carries no
 * range, deliberately, so one click clears everything outstanding. The screen, meanwhile, shows
 * one day or one week. Counting the returned rows therefore answers "did the sweep move
 * anything", which is not the question the owner asked — and the two answers come apart in a
 * mundane case: a person with a future PTO booking gets rows back from the sweep while the row
 * they are looking at has not moved. Reporting that as success is how a declined row that the
 * verb cannot touch came to look like a click that silently did nothing.
 *
 * So the period the owner is viewing decides between three honest outcomes: something here moved,
 * something moved but not here, or nothing moved at all.
 *
 * A fourth thing is worth saying in the latter two, and PIKA-730 narrowed it to exactly the case
 * that still needs it. `submittableStatusClause` now admits DECLINED for **time off**, so a
 * declined leave day inside the viewed period is swept by this very click and lands in the
 * success branch above — the hint neither fires nor should. What the sweep still cannot move is
 * declined *project* or *internal* time, which keeps RESUBMIT as its only verb. So when the
 * period offers Resubmit and nothing here moved, the declined time in front of the owner is the
 * kind Resubmit owns, the control that does the job is labelled for timesheets, and nothing else
 * on this surface says so — hence the hint.
 *
 * The hint keys off `offersResubmit` rather than a "has declined time off" predicate, and that
 * now reads as the right question rather than a near-enough proxy for it.
 *
 * @param submittedTimeRegs the mutation payload's `timeRegs` — every row the sweep moved, anywhere
 * @param currentViewingDate the day in view (its week, in Week view)
 * @param isWeekView whether the period is that whole week or the single day
 * @param offersResubmit whether the period is also showing a Resubmit button
 * @returns message ids to render in order, joined by a space
 */
export const getSubmitTimeOffToastMessageIds = ({
	submittedTimeRegs,
	currentViewingDate,
	isWeekView,
	offersResubmit,
}: {
	// Nullable throughout, as the payload types it: a row the graph could not date is simply
	// one this cannot place in the period, and Util.CreateNonUtcMomentDate already says so.
	submittedTimeRegs:
		| ReadonlyArray<{
				year: number | null;
				month: number | null;
				day: number | null;
		  } | null>
		| null
		| undefined;
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
	offersResubmit: boolean;
}): string[] => {
	const rangeStart = isWeekView ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone().startOf('day');
	const rangeEnd = isWeekView ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone().endOf('day');

	// One walk answers both questions the outcome needs — "did anything move" and "did anything
	// move here" — which matters because the sweep is unbounded and can hand back a backlog.
	let movedAnywhere = false;
	let movedInPeriod = false;
	for (const timeReg of submittedTimeRegs ?? []) {
		if (!timeReg) continue;
		movedAnywhere = true;
		const date = Util.CreateNonUtcMomentDate(timeReg.year, timeReg.month, timeReg.day);
		if (date?.isBetween(rangeStart, rangeEnd, 'day', '[]')) {
			movedInPeriod = true;
			break;
		}
	}

	if (movedInPeriod) return ['time_approval.submit_time_off_toast'];

	const outcome = movedAnywhere
		? 'time_approval.submit_time_off_other_periods_toast'
		: 'time_approval.submit_time_off_nothing_to_submit_toast';

	return offersResubmit ? [outcome, 'time_approval.submit_time_off_resubmit_hint'] : [outcome];
};

export const resubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	hasTimeRegsInWeek,
	hasApprovableTimeInWeek,
	intl,
}) => {
	trackEvent('Resubmit Timesheets Bottom', 'Clicked', {
		// Person id, not email address: an address is content, which PIKA-772 §1 keeps out.
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.id : 'myself',
		newStatusesFlag: hasFeatureFlag('timesheet_approval_new_statuses'),
		approvalType: APPROVAL_TYPE.RESUBMIT,
		// Every remaining Resubmit is a declined week with no time requiring approval, so this
		// should read true throughout. Recorded rather than assumed: it is the counter that says
		// how often the separate route is actually taken, and a false would mean the button is
		// reachable somewhere this change did not account for.
		noTimeRequiringApproval: !hasApprovableTimeInWeek,
	});
	Util.CommitMutation(
		UpdateApprovalStatusMutation,
		getResubmitInput({personId: selectedPerson.id, currentViewingDate, isWeekView, hasTimeRegsInWeek}),
		// RESUBMIT is one of the actions the back end rejects without the new-statuses
		// feature flag. It reports that in `errors` rather than by failing, so with no success
		// handler at all the button did nothing and said nothing.
		res => {
			if (showApprovalErrorModal(res.updateApprovalStatus?.errors)) return;
			// A partial failure is not a refusal, so it never reaches `errors`. This button has no
			// success toast to withhold, but without this the rows that rolled back are reported
			// nowhere at all and the week simply looks unchanged — the same silent success the
			// other four handlers now refuse.
			showPartialApprovalFailureModal(
				res.updateApprovalStatus?.failedTimeRegs,
				intl,
				(res.updateApprovalStatus?.timeRegs?.length ?? 0) === 0
			);
		}
	);
};

const unsubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
}) => {
	trackEvent('Unsubmit Timesheets Bottom', 'Clicked', {
		// Person id, not email address: an address is content, which PIKA-772 §1 keeps out.
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.id : 'myself',
		newStatusesFlag: hasFeatureFlag('timesheet_approval_new_statuses'),
		approvalType: APPROVAL_TYPE.UNSUBMIT,
	});
	const unsubmitDate = !isWeekView ? currentViewingDate : currentViewingDate.clone().startOf('week');
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: unsubmitDate,
		companyLockedDate,
		lockedDate,
	});
};

type TimesheetsApprovalButtonsProps = {
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	nonViewerPersonSelected: boolean;
	usingTimeApproval: boolean;
	usingTimeOffApproval: boolean;
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	lockedDate?: Moment.Moment;
	companyLockedDate?: Moment.Moment;
	lastWorkingDayInWeek;
	// Latest day in the viewed week holding a declined row, or null when the week holds none. Only
	// the current week reads it — see getSubmitEndDate for why a declined row is the one thing a
	// submit of the week you are standing in should reach past today for.
	lastDeclinedDateInWeek?: Moment.Moment | null;
	periodTimeApprovalStatus?: ApprovalStatus;
	hasTimeRegsInWeek: boolean;
	hasApprovableTimeInWeek: boolean;
	hasSubmittableTimeRegsInPeriod: boolean;
};
/**
 * What the Submit-time-off response means for the person who pressed the button.
 *
 * Exported for the same reason the other helpers in this file are: it is the part with rules in
 * it. `updateApprovalStatus` answers with three fields and this handler reads all three — a
 * refusal in `errors`, the rows that moved in `timeRegs`, and the rows that did not in
 * `failedTimeRegs`, which is the one that never reaches `errors` at all.
 */
export const handleSubmitTimeOffResponse = ({res, intl, currentViewingDate, isWeekView, offersResubmit}): void => {
	const {formatMessage} = intl;
	// A refusal comes back in `errors` with `persons` null — which the unguarded
	// `persons[0]` below used to turn into a TypeError, losing the message entirely.
	if (showApprovalErrorModal(res.updateApprovalStatus?.errors, true)) return;

	// A partial failure is not a refusal: the rows that moved still moved, so the
	// period-scoped toast below still runs — unless NOTHING moved, which `errors` cannot
	// report because that path reports per row. Without this the submitter was told
	// "Time offs submitted" over a batch where some rows rolled back.
	//
	// `closeOtherModals`, matching the refusal path just above. The confirmation modal
	// that drove this is `autoClosePositive`, so it has normally closed itself by the
	// time the mutation answers — but nothing here calls `closeModal()`, so replacing the
	// stack is free and keeps the warning readable if it has not.
	const nothingApplied = (res.updateApprovalStatus?.timeRegs?.length ?? 0) === 0;
	const hadFailures = showPartialApprovalFailureModal(res.updateApprovalStatus?.failedTimeRegs, intl, nothingApplied, true);
	if (hadFailures && nothingApplied) return;

	const personRes = res.updateApprovalStatus?.persons?.[0];
	if (!personRes) return;
	// Report what the mutation did to the period on screen, not merely that it ran.
	// The sweep is unbounded and the screen is not, so the returned rows have to be
	// read against the viewed period before any of this can be called a success —
	// see getSubmitTimeOffToastMessageIds for why, and for the Resubmit hint.
	//
	// Not flag-gated: which rows are submittable depends on the flag and the category
	// (`submittableStatusClause`, TimeApprovalLogic.java), but the question of whether
	// the sweep reached the period in view is the same either way.
	const messageIds = getSubmitTimeOffToastMessageIds({
		submittedTimeRegs: res.updateApprovalStatus?.timeRegs,
		currentViewingDate,
		isWeekView,
		offersResubmit,
	});
	// "Times offs submitted" is the one outcome that is still a short confirmation, and
	// 2.5s is sized for it. The period-scoped outcomes run to a sentence or two, and the
	// Resubmit hint — the only place the route out of a decline is named — lands last,
	// so those get the 5s the rest of the app uses for multi-clause toasts.
	const isShortConfirmation = messageIds.length === 1 && messageIds[0] === 'time_approval.submit_time_off_toast';
	createToast({
		duration: isShortConfirmation ? 2500 : 5000,
		message: messageIds.map(id => formatMessage({id})).join(' '),
	});
};

export const TimesheetsApprovalButtons = ({
	selectedPerson,
	nonViewerPersonSelected,
	usingTimeApproval,
	usingTimeOffApproval,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	lastWorkingDayInWeek,
	lastDeclinedDateInWeek = null,
	periodTimeApprovalStatus,
	hasTimeRegsInWeek,
	hasApprovableTimeInWeek,
	hasSubmittableTimeRegsInPeriod,
}: TimesheetsApprovalButtonsProps): JSX.Element | null => {
	const intl = useIntl();
	const {formatMessage} = intl;

	// The id is the Relay global id, as `getResubmitInput` also takes it — the graph runs
	// fromGlobalId on it, so a numeric id would not survive the round trip.
	const handleConfirmTimeOff = (selectedPersonId: string, offersResubmit: boolean) => {
		trackEvent('Submit Time Off Modal Save Button', 'Clicked');
		const onSuccess = res => handleSubmitTimeOffResponse({res, intl, currentViewingDate, isWeekView, offersResubmit});

		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const handleCloseTimeOffModal = () => {
		trackEvent('Submit Time Off Modal Cancel Button', 'Clicked');
	};

	// `offersResubmit` rides in from the caller rather than being read off `showResubmit`
	// directly: the action set is resolved further down, after the early return, and the toast
	// only needs to know what the period was offering at the moment of the click.
	const openSubmitTimeOffModal = ({
		nonViewerPersonSelected,
		selectedPerson,
		offersResubmit,
	}: {
		nonViewerPersonSelected: boolean;
		// TimesheetsPage_viewer['company']['person'], same shape the component takes.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedPerson: any;
		offersResubmit: boolean;
	}) => {
		trackEvent('Submit Time Off Bottom', 'Clicked', {
			// Person id, not email address: an address is content, which PIKA-772 §1 keeps out.
			timeRegPerson: nonViewerPersonSelected ? selectedPerson.id : 'myself',
			newStatusesFlag: hasFeatureFlag('timesheet_approval_new_statuses'),
			approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
		});

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'time_approval.submit_time_off'}),
			description: formatMessage({id: 'time_off_approval.submit_timesheets_info'}),
			autoClosePositive: true,
			autoCloseNegative: true,
			callbackPositive: () => handleConfirmTimeOff(selectedPerson.id, offersResubmit),
			callbackNegative: handleCloseTimeOffModal,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.SAVE,
		});
	};

	const isCompanyLocked = useMemo(() => {
		if (companyLockedDate) {
			return isWeekView
				? companyLockedDate.isSameOrAfter(currentViewingDate.clone().endOf('week'), 'd')
				: companyLockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, companyLockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	if (!usingTimeApproval && !usingTimeOffApproval) return null;

	const hasNewStatuses = hasFeatureFlag('timesheet_approval_new_statuses');

	const {showSubmit, showResubmit, showUnsubmit} = getTimesheetApprovalActions({
		periodApprovalStatus: periodTimeApprovalStatus ?? null,
		hasSubmittableTimeRegs: hasSubmittableTimeRegsInPeriod,
		hasApprovableTimeInWeek,
		hasNewStatuses,
		isWorkdaysLocked,
		usingTimeApproval,
	});

	const resubmitButton = (
		<Button
			emphasis={EMPHASIS.HIGH}
			onPress={() =>
				resubmitTimesheets({
					nonViewerPersonSelected,
					selectedPerson,
					isWeekView,
					currentViewingDate,
					hasTimeRegsInWeek,
					hasApprovableTimeInWeek,
					intl,
				})
			}
			data-cy={'resubmit-timesheets-button'}
		>
			{nonViewerPersonSelected
				? formatMessage({id: 'time_approval.resubmit_names_timesheets'}, {name: selectedPerson.firstName})
				: formatMessage({id: 'time_approval.resubmit_timesheets'})}
		</Button>
	);

	return usingTimeApproval ? (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
								offersResubmit: showResubmit,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showResubmit && resubmitButton}
			{showSubmit && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheets' + messageIdSuffix,
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheets' + messageIdSuffix,
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							submitTimesheets({
								nonViewerPersonSelected,
								selectedPerson,
								isWeekView,
								currentViewingDate,
								lockedDate,
								lastWorkingDayInWeek,
								lastDeclinedDateInWeek,
								usingTimeOffApproval,
								hasApprovableTimeInWeek,
							})
						}
						// Only a company lock can stop a submit. The owner's own submit lock must not:
						// it covers precisely the period whose outstanding time this button exists
						// to send on, so gating on it renders the button inert exactly when it is
						// needed. The server agrees — SUBMIT resolves its locked date without a
						// person, so it enforces the company lock alone.
						disabled={isCompanyLocked}
						data-cy={'submit-timesheets-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheet',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheet',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showUnsubmit && (
				// Steps down to secondary on the one path that can still offer two actions at once:
				// without the new statuses the choice is lock-driven, and a locked period that
				// holds unsubmitted rows offers Submit as well.
				<Button
					emphasis={showSubmit ? EMPHASIS.MEDIUM : EMPHASIS.HIGH}
					onPress={() =>
						unsubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							lockedDate,
							companyLockedDate,
						})
					}
					disabled={isCompanyLocked}
					data-cy={'unsubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage(
								{
									id: 'time_approval.unsubmit_names_timesheets',
								},
								{
									name: selectedPerson.firstName,
								}
						  )
						: formatMessage({
								id: 'time_approval.unsubmit_timesheets',
						  })}
				</Button>
			)}
		</>
	) : (
		<>
			{showResubmit && resubmitButton}
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
								offersResubmit: showResubmit,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	);
};
